import { themeAtom } from "../../../shared/atoms";
import { ExcelLikeGrid } from "../../../shared/excel-grid/components/ExcelLikeGrid";
import {
  ExcelGridCellCommit,
  ExcelGridColumn,
  ExcelGridRow,
} from "../../../shared/excel-grid/models/excel-grid.models";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import {
  ADMIN_UWHC_PROVIDER_COLUMNS,
  ADMIN_UWHC_PROVIDER_FIELDS,
  AdminUwhcProviderField,
  AdminUwhcProviderPayload,
  AdminUwhcProviderRow,
} from "../../models/admin-uwhc.models";
import {
  createAdminUwhcProvider,
  deleteAdminUwhcProvider,
  getAdminUwhcErrorMessage,
  getAdminUwhcProviders,
  updateAdminUwhcProvider,
} from "../../services/admin-uwhc.service";
import { adminStyles } from "../../styles/admin.styles";
import {
  getProviderDisplayName,
  normalizeProviderValue,
  toAdminUwhcGridRow,
  toAdminUwhcProviderPayload,
} from "../../utils/admin-uwhc.utils";
import AdminUwhcProviderModal from "./AdminUwhcProviderModal";
import AdminUwhcViewTabs from "./AdminUwhcViewTabs";
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

type LoadingState = "loading" | "ready" | "error";
type ProviderModalState = { mode: "create" | "edit"; row: AdminUwhcProviderRow | null };
// Stores the value the user should keep seeing while its save is still in flight.
// The token lets one field be edited again before the older response returns, without the older response clearing the newer value.
type PendingProviderCell = { value: string | null; token: number };
type PendingProviderPatch = Partial<Record<AdminUwhcProviderField, PendingProviderCell>>;

/**
 * Admin provider editor that saves each confirmed cell edit through backend-owned Snowflake procedures.
 */
const AdminUwhcProvidersPanel = () => {
  const theme = useAtomValue(themeAtom);
  const [rows, setRows] = useState<AdminUwhcProviderRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<AdminUwhcProviderRow | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<AdminUwhcProviderRow | null>(null);
  const [providerModal, setProviderModal] = useState<ProviderModalState | null>(null);
  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});
  const [isModalSaving, setIsModalSaving] = useState(false);
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [modalErrorMessage, setModalErrorMessage] = useState<string | null>(null);
  // Keeps saves for one provider in user order.
  // The backend now accepts small patches, but two edits for the same row should still arrive in the order the user made them.
  const providerSaveQueuesRef = useRef<Record<string, Promise<void>>>({});
  // Keeps newer values visible while older backend confirmations are still catching up.
  // Without this, the first 200 response would redraw the row without the user's later pending edits.
  const pendingProviderPatchesRef = useRef<Record<string, PendingProviderPatch>>({});
  const providerSaveTokenRef = useRef(0);

  logV("AdminUwhcProvidersPanel - BUILD", { count: rows.length, loadingState });

  const loadProviders = useCallback(async () => {
    log("AdminUwhcProvidersPanel - loadProviders");
    setLoadingState("loading");
    setErrorMessage(null);

    try {
      const response = await getAdminUwhcProviders();
      const nextRows = response.rows.map((row) => toAdminUwhcGridRow(row));
      log("STATE - applyProviders", { count: nextRows.length });
      setRows(nextRows);
      setLoadingState("ready");
    } catch (error) {
      logE(error);
      setErrorMessage(getAdminUwhcErrorMessage(error));
      setLoadingState("error");
    }
  }, []);

  useEffect(() => {
    void loadProviders();
  }, [loadProviders]);

  // Chains saves per grid row without blocking edits on other rows.
  // A failed save must not poison the row queue, so the next edit still gets a chance to reach the backend.
  const enqueueProviderSave = useCallback((gridRowId: string, save: () => Promise<void>) => {
    const previousSave = providerSaveQueuesRef.current[gridRowId] ?? Promise.resolve();
    const nextSave = previousSave.catch(() => undefined).then(save);
    providerSaveQueuesRef.current[gridRowId] = nextSave;
    void nextSave.finally(() => {
      if (providerSaveQueuesRef.current[gridRowId] === nextSave) {
        delete providerSaveQueuesRef.current[gridRowId];
      }
    });
    return nextSave;
  }, []);

  const handleCellCommit = useCallback(
    async ({ row, column, value }: ExcelGridCellCommit) => {
      if (column.editable === false) {
        log("AdminUwhcProvidersPanel - handleCellCommit - readonly", { column: column.key });
        return;
      }

      const field = column.key as AdminUwhcProviderField;
      const previousRow = row as AdminUwhcProviderRow;
      const nextRow: AdminUwhcProviderRow = {
        ...previousRow,
        [field]: normalizeProviderValue(value),
      };
      const cellKey = getCellKey(previousRow, column);
      // Gives this exact save attempt a handle that can be matched when the backend responds.
      // Fast edits can queue several saves for the same row, so field name alone is not enough to know what can be cleared.
      const saveToken = providerSaveTokenRef.current + 1;
      providerSaveTokenRef.current = saveToken;
      parkPendingProviderValue(previousRow.gridRowId, field, value, saveToken, pendingProviderPatchesRef.current);

      log("AdminUwhcProvidersPanel - handleCellCommit", { field, hasProviderUid: Boolean(previousRow.PROVIDER_UID) });
      setErrorMessage(null);
      setSavingCells((current) => ({ ...current, [cellKey]: true }));
      log("STATE - applyProviderCellOptimistic", { field });
      setRows((current) =>
        current.map((provider) => (provider.gridRowId === previousRow.gridRowId ? nextRow : provider)),
      );

      await enqueueProviderSave(previousRow.gridRowId, async () => {
        try {
          const providerIdentifier = getProviderIdentifierForMutation(nextRow);
          // Existing rows send only the committed cell.
          // Draft rows still send the full row once, because create needs enough data to build the first historical version.
          const payload = providerIdentifier
            ? getAdminUwhcProviderPatchPayload(field, value)
            : toAdminUwhcProviderPayload(nextRow);
          const response = providerIdentifier
            ? await updateAdminUwhcProvider(providerIdentifier, payload)
            : await createAdminUwhcProvider(payload);
          const savedRow = toAdminUwhcGridRow(response.row, previousRow.gridRowId);
          // The confirmed backend row can be older than what the user already typed into other cells.
          // Reapply still-pending cells before drawing, so the row never flashes backward through earlier save states.
          removePendingProviderValue(previousRow.gridRowId, field, saveToken, pendingProviderPatchesRef.current);
          const visibleRow = applyPendingProviderPatch(
            savedRow,
            previousRow.gridRowId,
            pendingProviderPatchesRef.current,
          );

          log("STATE - applyProviderCellSave", { field });
          setRows((current) =>
            current.map((provider) => (provider.gridRowId === previousRow.gridRowId ? visibleRow : provider)),
          );
          setSelectedRow(visibleRow);
        } catch (error) {
          // Only this failed edit rolls back.
          // Any newer pending cells stay visible because the user has already seen and committed those values.
          removePendingProviderValue(previousRow.gridRowId, field, saveToken, pendingProviderPatchesRef.current);
          const rollbackRow = applyPendingProviderPatch(
            previousRow,
            previousRow.gridRowId,
            pendingProviderPatchesRef.current,
          );

          logE(error);
          log("STATE - rollbackProviderCell", { field });
          setRows((current) =>
            current.map((provider) => (provider.gridRowId === previousRow.gridRowId ? rollbackRow : provider)),
          );
          setErrorMessage(getAdminUwhcErrorMessage(error));
        } finally {
          setSavingCells((current) => {
            const { [cellKey]: _removed, ...remaining } = current;
            return remaining;
          });
        }
      });
    },
    [enqueueProviderSave],
  );

  const handleAddRow = useCallback(() => {
    log("AdminUwhcProvidersPanel - handleAddRow");
    setModalErrorMessage(null);
    setProviderModal({ mode: "create", row: null });
  }, []);

  const handleEditSelected = useCallback(() => {
    if (!selectedRow) {
      log("AdminUwhcProvidersPanel - handleEditSelected - no selection");
      return;
    }

    log("AdminUwhcProvidersPanel - handleEditSelected", { providerUid: selectedRow.PROVIDER_UID });
    setModalErrorMessage(null);
    setProviderModal({ mode: "edit", row: selectedRow });
  }, [selectedRow]);

  const handleDeleteSelected = useCallback(() => {
    if (selectedRow) {
      log("AdminUwhcProvidersPanel - handleDeleteSelected", { providerUid: selectedRow.PROVIDER_UID });
      setDeleteCandidate(selectedRow);
    }
  }, [selectedRow]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteCandidate) {
      log("AdminUwhcProvidersPanel - handleConfirmDelete - no candidate");
      return;
    }

    log("AdminUwhcProvidersPanel - handleConfirmDelete", { providerUid: deleteCandidate.PROVIDER_UID });
    setErrorMessage(null);

    try {
      const providerIdentifier = getProviderIdentifierForMutation(deleteCandidate);

      if (!providerIdentifier) {
        log("STATE - removeDraftProvider", { gridRowId: deleteCandidate.gridRowId });
        setRows((current) => current.filter((row) => row.gridRowId !== deleteCandidate.gridRowId));
        setDeleteCandidate(null);
        setSelectedRow(null);
        return;
      }

      await deleteAdminUwhcProvider(providerIdentifier);
      log("STATE - removeProvider", { providerIdentifier });
      setRows((current) => current.filter((row) => row.gridRowId !== deleteCandidate.gridRowId));
      setDeleteCandidate(null);
      setSelectedRow(null);
    } catch (error) {
      logE(error);
      setErrorMessage(getAdminUwhcErrorMessage(error));
    }
  }, [deleteCandidate]);

  const handleSelectedRowChange = useCallback((row: ExcelGridRow | null) => {
    logV("AdminUwhcProvidersPanel - handleSelectedRowChange", { hasRow: Boolean(row) });
    setSelectedRow(row as AdminUwhcProviderRow | null);
  }, []);

  const handleModalSave = useCallback(
    async (payload: AdminUwhcProviderPayload) => {
      if (!providerModal) {
        log("AdminUwhcProvidersPanel - handleModalSave - no modal");
        return;
      }

      log("AdminUwhcProvidersPanel - handleModalSave", { mode: providerModal.mode });
      setIsModalSaving(true);
      setModalErrorMessage(null);
      setErrorMessage(null);

      try {
        const providerIdentifier = providerModal.row ? getProviderIdentifierForMutation(providerModal.row) : null;
        // The modal edits many fields at once, but it still sends only fields that actually changed.
        // This keeps the modal from overwriting a faster cell edit that saved a different column first.
        const providerPayload =
          providerModal.mode === "edit" && providerModal.row
            ? getChangedAdminUwhcProviderPayload(providerModal.row, payload)
            : payload;

        if (providerModal.mode === "edit" && Object.keys(providerPayload).length === 0) {
          setProviderModal(null);
          return;
        }

        const response =
          providerModal.mode === "edit" && providerIdentifier
            ? await updateAdminUwhcProvider(providerIdentifier, providerPayload)
            : await createAdminUwhcProvider(providerPayload);
        const savedRow = toAdminUwhcGridRow(response.row, providerModal.row?.gridRowId);

        log("STATE - applyProviderModalSave", { mode: providerModal.mode });
        setRows((current) => {
          if (providerModal.mode === "edit" && providerModal.row) {
            return current.map((row) => (row.gridRowId === providerModal.row?.gridRowId ? savedRow : row));
          }

          return [savedRow, ...current];
        });
        setSelectedRow(savedRow);
        setProviderModal(null);
      } catch (error) {
        logE(error);
        setModalErrorMessage(getAdminUwhcErrorMessage(error));
      } finally {
        setIsModalSaving(false);
      }
    },
    [providerModal],
  );

  const isSavingCell = useCallback(
    (row: ExcelGridRow, column: ExcelGridColumn) => Boolean(savingCells[getCellKey(row, column)]),
    [savingCells],
  );

  const providerCountLabel = useMemo(() => `${rows.length} active row${rows.length === 1 ? "" : "s"}`, [rows.length]);

  return (
    <main
      data-test="admin-uwhc-page"
      className={twMerge("flex min-h-0 flex-1 overflow-hidden", theme.home.tailwind.textDefault)}
    >
      <div
        className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
        data-test="admin-uwhc-provider-admin"
      >
        <AdminUwhcViewTabs />
        <AdminUwhcTopBar
          providerCountLabel={providerCountLabel}
          selectedRow={selectedRow}
          isRefreshing={loadingState === "loading"}
          onAddRow={handleAddRow}
          onEditSelected={handleEditSelected}
          onDeleteSelected={handleDeleteSelected}
          onRefresh={loadProviders}
        />
        <AdminUwhcStatus errorMessage={errorMessage} loadingState={loadingState} />
        <AdminUwhcGrid
          rows={rows}
          loadingState={loadingState}
          onCellCommit={handleCellCommit}
          onSelectedRowChange={handleSelectedRowChange}
          isSavingCell={isSavingCell}
        />
      </div>
      <DeleteProviderModal
        row={deleteCandidate}
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={handleConfirmDelete}
      />
      {providerModal && (
        <AdminUwhcProviderModal
          mode={providerModal.mode}
          row={providerModal.row}
          rows={rows}
          saving={isModalSaving}
          errorMessage={modalErrorMessage}
          onCancel={() => {
            if (!isModalSaving) {
              log("AdminUwhcProviderModal - onCancel", { mode: providerModal.mode });
              setProviderModal(null);
              setModalErrorMessage(null);
            }
          }}
          onSave={handleModalSave}
        />
      )}
    </main>
  );
};

// === PAGE PARTS ===

const AdminUwhcTopBar = ({
  providerCountLabel,
  selectedRow,
  isRefreshing,
  onAddRow,
  onEditSelected,
  onDeleteSelected,
  onRefresh,
}: {
  providerCountLabel: string;
  selectedRow: AdminUwhcProviderRow | null;
  isRefreshing: boolean;
  onAddRow: () => void;
  onEditSelected: () => void;
  onDeleteSelected: () => void;
  onRefresh: () => void;
}) => {
  const theme = useAtomValue(themeAtom);

  return (
    <header
      data-test="admin-uwhc-top-bar"
      className="relative z-40 flex h-11 flex-none items-center border-b border-gray-200 bg-white px-3 dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden pr-3">
        <h1 className={twMerge("min-w-0 truncate text-sm font-semibold leading-5", theme.home.tailwind.textDefault)}>
          UWHC Provider Admin
        </h1>
        <span
          className="hidden flex-none border border-[#D7E2DE] px-2 py-1 text-[11px] font-semibold text-[#4A5655] md:inline-flex"
          data-test="admin-uwhc-provider-count"
        >
          {providerCountLabel}
        </span>
      </div>
      <div className="ml-3 flex h-full flex-none items-center gap-2 bg-white pl-2 dark:bg-gray-800">
        <button
          type="button"
          aria-label="Refresh UWHC providers"
          title="Refresh"
          data-test="admin-uwhc-refresh"
          disabled={isRefreshing}
          onClick={onRefresh}
          className="inline-flex h-8 w-8 items-center justify-center border border-gray-300 text-green transition-colors hover:bg-[#E9F4F3] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700"
        >
          <ArrowPathIcon className={twMerge("h-4 w-4", isRefreshing && "animate-spin")} />
        </button>
        <button
          type="button"
          data-test="admin-uwhc-add-row"
          onClick={onAddRow}
          className="inline-flex h-8 min-w-8 items-center justify-center gap-1.5 bg-green px-2 text-xs font-semibold text-white transition-colors hover:bg-[#166b60] 2xl:px-3"
        >
          <PlusIcon className="h-4 w-4" />
          <span className="hidden xl:inline">Add Row</span>
        </button>
        <button
          type="button"
          data-test="admin-uwhc-edit-row"
          disabled={!selectedRow}
          onClick={onEditSelected}
          className="inline-flex h-8 min-w-8 items-center justify-center gap-1.5 border border-gray-300 px-2 text-xs font-semibold transition-colors hover:bg-[#F7FBFA] disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 2xl:px-3"
        >
          <PencilSquareIcon className="h-4 w-4" />
          <span className="hidden xl:inline">Edit</span>
        </button>
        <button
          type="button"
          data-test="admin-uwhc-delete-row"
          disabled={!selectedRow}
          onClick={onDeleteSelected}
          className="inline-flex h-8 min-w-8 items-center justify-center gap-1.5 border border-[#BF0002] px-2 text-xs font-semibold text-[#BF0002] transition-colors hover:bg-[#FFF1F1] disabled:cursor-not-allowed disabled:opacity-40 2xl:px-3"
        >
          <TrashIcon className="h-4 w-4" />
          <span className="hidden xl:inline">Delete</span>
        </button>
      </div>
    </header>
  );
};

const AdminUwhcStatus = ({
  errorMessage,
  loadingState,
}: {
  errorMessage: string | null;
  loadingState: LoadingState;
}) => {
  if (errorMessage) {
    return (
      <div
        role="alert"
        data-test="admin-uwhc-error"
        className="border border-[#BF0002] bg-[#FFF1F1] px-4 py-3 text-sm text-[#8F0002]"
      >
        {errorMessage}
      </div>
    );
  }

  if (loadingState === "loading") {
    return (
      <div data-test="admin-uwhc-loading" className={twMerge(adminStyles.card.surface, "px-4 py-3 text-sm")}>
        Loading UWHC providers...
      </div>
    );
  }

  return null;
};

const AdminUwhcGrid = ({
  rows,
  loadingState,
  onCellCommit,
  onSelectedRowChange,
  isSavingCell,
}: {
  rows: AdminUwhcProviderRow[];
  loadingState: LoadingState;
  onCellCommit: (commit: ExcelGridCellCommit) => void | Promise<void>;
  onSelectedRowChange: (row: ExcelGridRow | null) => void;
  isSavingCell: (row: ExcelGridRow, column: ExcelGridColumn) => boolean;
}) => {
  logV("AdminUwhcGrid - BUILD", { count: rows.length, loadingState });

  if (loadingState === "loading") {
    return <div className="min-h-0 flex-1" data-test="admin-uwhc-grid-loading-boundary" />;
  }

  return (
    <ExcelLikeGrid
      rows={rows}
      columns={ADMIN_UWHC_PROVIDER_COLUMNS}
      dataTest="admin-uwhc-grid"
      emptyLabel="No active UWHC providers found."
      onCellCommit={onCellCommit}
      onSelectedRowChange={onSelectedRowChange}
      isSavingCell={isSavingCell}
    />
  );
};

const DeleteProviderModal = ({
  row,
  onCancel,
  onConfirm,
}: {
  row: AdminUwhcProviderRow | null;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  if (!row) {
    return null;
  }

  logV("DeleteProviderModal - BUILD", { providerUid: row.PROVIDER_UID });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-uwhc-delete-title"
      data-test="admin-uwhc-delete-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    >
      <section className="w-full max-w-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 flex-none text-[#BF0002]" />
          <div className="min-w-0">
            <h2 id="admin-uwhc-delete-title" className="text-lg font-semibold">
              Delete Provider Row
            </h2>
            <p className="mt-2 text-sm leading-6 opacity-80" data-test="admin-uwhc-delete-provider-name">
              {getProviderDisplayName(row)}
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            data-test="admin-uwhc-delete-cancel"
            onClick={onCancel}
            className="h-10 border border-gray-300 px-4 text-sm font-semibold dark:border-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            data-test="admin-uwhc-delete-confirm"
            onClick={onConfirm}
            className="inline-flex h-10 items-center gap-2 bg-[#BF0002] px-4 text-sm font-semibold text-white"
          >
            <TrashIcon className="h-5 w-5" />
            Delete
          </button>
        </div>
      </section>
    </div>
  );
};

function getCellKey(row: ExcelGridRow, column: ExcelGridColumn): string {
  return `${row.gridRowId}:${column.key}`;
}

function getProviderIdentifierForMutation(row: AdminUwhcProviderRow): string | null {
  const providerUid = row.PROVIDER_UID?.trim();

  if (providerUid) {
    return providerUid;
  }

  // Lets older active rows repair themselves during the next edit.
  // The backend recognizes this as a legacy row id, creates a provider uid, and writes the next version with that uid.
  if (typeof row.ID === "number") {
    return String(row.ID);
  }

  return null;
}

function getAdminUwhcProviderPatchPayload(field: AdminUwhcProviderField, value: unknown): AdminUwhcProviderPayload {
  // Sends the smallest possible edit so a late response cannot carry stale values for other columns.
  return { [field]: normalizeProviderValue(value) };
}

function getChangedAdminUwhcProviderPayload(
  row: AdminUwhcProviderRow,
  payload: AdminUwhcProviderPayload,
): AdminUwhcProviderPayload {
  // Converts the full modal form back into a patch.
  // Unchanged values are intentionally absent, not present as null, so the backend preserves them.
  return ADMIN_UWHC_PROVIDER_FIELDS.reduce<AdminUwhcProviderPayload>((provider, field) => {
    const nextValue = normalizeProviderValue(payload[field]);

    if (normalizeProviderValue(row[field]) !== nextValue) {
      provider[field] = nextValue;
    }

    return provider;
  }, {});
}

function parkPendingProviderValue(
  gridRowId: string,
  field: AdminUwhcProviderField,
  value: unknown,
  token: number,
  patches: Record<string, PendingProviderPatch>,
) {
  // Parks the latest value by field instead of by request.
  // If the same field is edited twice quickly, the newer token replaces the older one and remains the visible truth.
  patches[gridRowId] = {
    ...patches[gridRowId],
    [field]: { value: normalizeProviderValue(value), token },
  };
}

function removePendingProviderValue(
  gridRowId: string,
  field: AdminUwhcProviderField,
  token: number,
  patches: Record<string, PendingProviderPatch>,
) {
  const patch = patches[gridRowId];

  // Ignores stale responses for a field that has already been edited again.
  // The older save may still succeed, but it no longer owns the pending value shown in the grid.
  if (!patch || patch[field]?.token !== token) {
    return;
  }

  const { [field]: _removed, ...remainingPatch } = patch;

  if (Object.keys(remainingPatch).length === 0) {
    delete patches[gridRowId];
    return;
  }

  patches[gridRowId] = remainingPatch;
}

function applyPendingProviderPatch(
  row: AdminUwhcProviderRow,
  gridRowId: string,
  patches: Record<string, PendingProviderPatch>,
): AdminUwhcProviderRow {
  const patch = patches[gridRowId];

  if (!patch) {
    return row;
  }

  // Draws the backend-confirmed row plus the user's still-unconfirmed edits.
  // This keeps the visible row stable while Snowflake historisation returns one completed save at a time.
  return Object.entries(patch).reduce<AdminUwhcProviderRow>(
    (provider, [field, pendingCell]) => ({
      ...provider,
      [field]: pendingCell?.value ?? null,
    }),
    row,
  );
}

export default AdminUwhcProvidersPanel;
